import { useEffect, useState } from 'react';
import { getEmailReportsSummary } from '../../../api/adminEmailCenter';
import EmailCenterPage, { EmptyState, PageSection, StatusBadge } from './EmailCenterShared';

export default function EmailReports() {
  const [state, setState] = useState({ loading: true, error: '', data: null });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getEmailReportsSummary();
        if (!active) return;
        setState({ loading: false, error: '', data });
      } catch (error) {
        if (!active) return;
        setState({ loading: false, error: error.message || 'Failed to load reports', data: null });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const totals = state.data?.totals || {};

  return (
    <EmailCenterPage title="Analytics & Reports" description="Campaign performance, failure clusters, and admin-side action history for audit and optimization.">
      {state.error ? <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{state.error}</div> : null}

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Sent</p>
          <div className="mt-4 text-3xl font-semibold text-slate-900">{totals.sent || 0}</div>
        </div>
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Failed</p>
          <div className="mt-4 text-3xl font-semibold text-slate-900">{totals.failed || 0}</div>
        </div>
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Queued</p>
          <div className="mt-4 text-3xl font-semibold text-slate-900">{totals.queued || 0}</div>
        </div>
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Skipped</p>
          <div className="mt-4 text-3xl font-semibold text-slate-900">{totals.skipped || 0}</div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <PageSection title="Campaign Performance" subtitle="Recent campaign totals and terminal state snapshot.">
          {!state.data?.campaigns?.length && !state.loading ? (
            <EmptyState title="No campaign reports yet" body="Campaign metrics will show here once drafts are queued and processed." />
          ) : (
            <div className="space-y-3">
              {(state.data?.campaigns || []).map((campaign) => (
                <div key={`${campaign.campaignName}-${campaign.updatedAt}`} className="rounded-[24px] border border-slate-100 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{campaign.campaignName}</p>
                      <p className="mt-1 text-sm text-slate-500">
                        sent {campaign.sentCount} | failed {campaign.failedCount} | queued {campaign.queuedCount}
                      </p>
                    </div>
                    <StatusBadge value={campaign.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </PageSection>

        <PageSection title="Failure Insights" subtitle="Top grouped reasons currently impacting delivery success.">
          {!state.data?.topFailureReasons?.length && !state.loading ? (
            <EmptyState title="No failure clusters" body="As soon as failures are logged, the most common reasons will be grouped here." />
          ) : (
            <div className="space-y-3">
              {(state.data?.topFailureReasons || []).map((row) => (
                <div key={row.reason} className="flex items-center justify-between rounded-[24px] border border-rose-100 bg-rose-50/60 px-4 py-3 text-sm">
                  <span className="max-w-[80%] text-rose-700">{row.reason}</span>
                  <span className="font-semibold text-rose-800">{row.total}</span>
                </div>
              ))}
            </div>
          )}
        </PageSection>
      </div>

      <PageSection title="Admin Action History" subtitle="Recent email-related admin actions from audit logs.">
        {!state.data?.adminActions?.length && !state.loading ? (
          <EmptyState title="No email audit actions yet" body="Create, update, send, and retry actions will surface here as soon as admins use the module." />
        ) : (
          <div className="space-y-3">
            {(state.data?.adminActions || []).map((action, index) => (
              <div key={`${action.action}-${action.created_at}-${index}`} className="rounded-[24px] border border-slate-100 bg-slate-50/60 p-4">
                <p className="font-semibold text-slate-900">{action.action}</p>
                <p className="mt-2 text-sm text-slate-500">{action.created_at}</p>
                {action.meta ? <pre className="mt-3 overflow-x-auto rounded-2xl bg-slate-900 p-3 text-xs text-slate-100">{JSON.stringify(action.meta, null, 2)}</pre> : null}
              </div>
            ))}
          </div>
        )}
      </PageSection>
    </EmailCenterPage>
  );
}
